import React, { useEffect, useState } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  FormControl,
  FormLabel,
  useColorModeValue,
  useToast,
  Alert,
  AlertIcon,
  AlertDescription,
  IconButton,
  InputGroup,
  InputRightElement,
  Switch,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase/config";
import PageShell from "../layout/PageShell";
import { useAuth } from "../contexts/AuthContext";

const LEGACY_GOOGLE_CONFIG_COLLECTION = "googleConfig";
const LEGACY_GOOGLE_CONFIG_DOC_ID = "global";

const GOOGLE_MAPS_CONFIG_COLLECTION = "googleMapsConfig";
const GOOGLE_MAPS_CONFIG_DOC_ID = "global";

const RECAPTCHA_CONFIG_COLLECTION = "recaptchaConfig";
const RECAPTCHA_CONFIG_DOC_ID = "global";

const GOOGLE_TTS_CONFIG_COLLECTION = "googleTtsConfig";
const GOOGLE_TTS_CONFIG_DOC_ID = "global";

const GOOGLE_TRANSLATE_CONFIG_COLLECTION = "googleTranslateConfig";
const GOOGLE_TRANSLATE_CONFIG_DOC_ID = "global";

interface GoogleConfig {
  mapsApiKey?: string;
  mapsBrowserKey?: string;
  placesApiKey?: string;
  geocodingApiKey?: string;
  directionsApiKey?: string;
  addressValidationApiKey?: string;
  analyticsMeasurementId?: string;
  tagManagerContainerId?: string;
  recaptchaSiteKey?: string;
  recaptchaSecretKey?: string;
  lastUpdated?: any;
  updatedBy?: string;
}

const GoogleSettings: React.FC = () => {
  const toast = useToast();
  const { currentUser } = useAuth();

  const [mapsApiKey, setMapsApiKey] = useState("");
  const [mapsBrowserKey, setMapsBrowserKey] = useState("");
  const [placesApiKey, setPlacesApiKey] = useState("");
  const [geocodingApiKey, setGeocodingApiKey] = useState("");
  const [directionsApiKey, setDirectionsApiKey] = useState("");
  const [addressValidationApiKey, setAddressValidationApiKey] = useState("");
  const [analyticsMeasurementId, setAnalyticsMeasurementId] = useState("");
  const [tagManagerContainerId, setTagManagerContainerId] = useState("");
  const [recaptchaSiteKey, setRecaptchaSiteKey] = useState("");
  const [recaptchaSecretKey, setRecaptchaSecretKey] = useState("");

  const [savingMaps, setSavingMaps] = useState(false);
  const [savingRecaptcha, setSavingRecaptcha] = useState(false);
  const [ttsEnabled, setTtsEnabled] = useState(false);
  const [ttsApiKey, setTtsApiKey] = useState("");
  const [savingTts, setSavingTts] = useState(false);
  const [showTtsApiKey, setShowTtsApiKey] = useState(false);
  const [translateApiKey, setTranslateApiKey] = useState("");
  const [savingTranslate, setSavingTranslate] = useState(false);
  const [showTranslateApiKey, setShowTranslateApiKey] = useState(false);

  const [showMapsApiKey, setShowMapsApiKey] = useState(false);
  const [showMapsBrowserKey, setShowMapsBrowserKey] = useState(false);
  const [showAddressValidationApiKey, setShowAddressValidationApiKey] = useState(false);
  const [showRecaptchaSecretKey, setShowRecaptchaSecretKey] = useState(false);

  const bgPage = useColorModeValue("gray.50", "gray.900");
  const bgCard = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const textColor = useColorModeValue("gray.700", "white");
  const textSecondary = useColorModeValue("gray.500", "gray.400");

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const mapsDocRef = doc(db, GOOGLE_MAPS_CONFIG_COLLECTION, GOOGLE_MAPS_CONFIG_DOC_ID);
        const recaptchaDocRef = doc(db, RECAPTCHA_CONFIG_COLLECTION, RECAPTCHA_CONFIG_DOC_ID);
        const ttsDocRef = doc(db, GOOGLE_TTS_CONFIG_COLLECTION, GOOGLE_TTS_CONFIG_DOC_ID);
        const translateDocRef = doc(db, GOOGLE_TRANSLATE_CONFIG_COLLECTION, GOOGLE_TRANSLATE_CONFIG_DOC_ID);
        const [mapsDoc, recaptchaDoc, ttsDoc, translateDoc] = await Promise.all([
          getDoc(mapsDocRef),
          getDoc(recaptchaDocRef),
          getDoc(ttsDocRef),
          getDoc(translateDocRef),
        ]);

        const gotAny = mapsDoc.exists() || recaptchaDoc.exists();

        if (mapsDoc.exists()) {
          const data = mapsDoc.data() as GoogleConfig;
          setMapsApiKey(data.mapsApiKey || "");
          setMapsBrowserKey(data.mapsBrowserKey || "");
          setPlacesApiKey(data.placesApiKey || "");
          setGeocodingApiKey(data.geocodingApiKey || "");
          setDirectionsApiKey(data.directionsApiKey || "");
          setAddressValidationApiKey(data.addressValidationApiKey || "");
          setAnalyticsMeasurementId(data.analyticsMeasurementId || "");
          setTagManagerContainerId(data.tagManagerContainerId || "");
        }

        if (recaptchaDoc.exists()) {
          const data = recaptchaDoc.data() as GoogleConfig;
          setRecaptchaSiteKey(data.recaptchaSiteKey || "");
          setRecaptchaSecretKey(data.recaptchaSecretKey || "");
        }

        if (ttsDoc.exists()) {
          const data = ttsDoc.data() as { enabled?: boolean; apiKey?: string };
          setTtsEnabled(data.enabled === true);
          setTtsApiKey(data.apiKey || "");
        }

        if (translateDoc.exists()) {
          const data = translateDoc.data() as { apiKey?: string };
          setTranslateApiKey(data.apiKey || "");
        }

        if (!gotAny) {
          const legacyRef = doc(db, LEGACY_GOOGLE_CONFIG_COLLECTION, LEGACY_GOOGLE_CONFIG_DOC_ID);
          const legacyDoc = await getDoc(legacyRef);
          if (legacyDoc.exists()) {
            const data = legacyDoc.data() as GoogleConfig;
            setMapsApiKey(data.mapsApiKey || "");
            setMapsBrowserKey((data as any).mapsBrowserKey || "");
            setPlacesApiKey(data.placesApiKey || "");
            setGeocodingApiKey(data.geocodingApiKey || "");
            setDirectionsApiKey(data.directionsApiKey || "");
            setAddressValidationApiKey(data.addressValidationApiKey || "");
            setAnalyticsMeasurementId(data.analyticsMeasurementId || "");
            setTagManagerContainerId(data.tagManagerContainerId || "");
            setRecaptchaSiteKey(data.recaptchaSiteKey || "");
            setRecaptchaSecretKey(data.recaptchaSecretKey || "");
          }
        }
      } catch (error) {
        console.error("Error loading Google config:", error);
      }
    };

    loadConfig();
  }, []);

  const handleSaveMaps = async () => {
    setSavingMaps(true);
    try {
      const updateData: any = {
        mapsApiKey: mapsApiKey.trim(),
        mapsBrowserKey: mapsBrowserKey.trim(),
        placesApiKey: placesApiKey.trim(),
        geocodingApiKey: geocodingApiKey.trim(),
        directionsApiKey: directionsApiKey.trim(),
        addressValidationApiKey: addressValidationApiKey.trim(),
        analyticsMeasurementId: analyticsMeasurementId.trim(),
        tagManagerContainerId: tagManagerContainerId.trim(),
        lastUpdated: serverTimestamp(),
        updatedBy: currentUser?.email || "unknown",
      };

      const configDocRef = doc(db, GOOGLE_MAPS_CONFIG_COLLECTION, GOOGLE_MAPS_CONFIG_DOC_ID);
      await setDoc(configDocRef, updateData, { merge: true });

      toast({
        title: "Success",
        description: "Google Maps settings saved successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error("Error saving Google Maps config:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to save Google Maps settings",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSavingMaps(false);
    }
  };

  const handleSaveRecaptcha = async () => {
    setSavingRecaptcha(true);
    try {
      const updateData: any = {
        recaptchaSiteKey: recaptchaSiteKey.trim(),
        recaptchaSecretKey: recaptchaSecretKey.trim(),
        lastUpdated: serverTimestamp(),
        updatedBy: currentUser?.email || "unknown",
      };

      const configDocRef = doc(db, RECAPTCHA_CONFIG_COLLECTION, RECAPTCHA_CONFIG_DOC_ID);
      await setDoc(configDocRef, updateData, { merge: true });

      toast({
        title: "Success",
        description: "reCAPTCHA settings saved successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error("Error saving reCAPTCHA config:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to save reCAPTCHA settings",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSavingRecaptcha(false);
    }
  };

  const handleSaveTts = async () => {
    setSavingTts(true);
    try {
      const updateData: any = {
        enabled: ttsEnabled,
        apiKey: ttsApiKey.trim(),
        lastUpdated: serverTimestamp(),
        updatedBy: currentUser?.email || "unknown",
      };

      const configDocRef = doc(db, GOOGLE_TTS_CONFIG_COLLECTION, GOOGLE_TTS_CONFIG_DOC_ID);
      await setDoc(configDocRef, updateData, { merge: true });

      toast({
        title: "Success",
        description: "Text-to-Speech settings saved successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error("Error saving Google TTS config:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to save Text-to-Speech settings",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSavingTts(false);
    }
  };

  const handleSaveTranslate = async () => {
    setSavingTranslate(true);
    try {
      const configDocRef = doc(db, GOOGLE_TRANSLATE_CONFIG_COLLECTION, GOOGLE_TRANSLATE_CONFIG_DOC_ID);
      await setDoc(
        configDocRef,
        {
          apiKey: translateApiKey.trim(),
          lastUpdated: serverTimestamp(),
          updatedBy: currentUser?.email || "unknown",
        },
        { merge: true }
      );
      toast({
        title: "Success",
        description: "Cloud Translation settings saved successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error("Error saving Google Translation config:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to save Cloud Translation settings",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSavingTranslate(false);
    }
  };

  return (
    <PageShell
      title="Google Settings"
      description="Project-wide Google credentials used by the online website and other services."
    >
      <VStack spacing={4} align="stretch">
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <AlertDescription>
            These settings are stored project-wide in Firestore and can be used later by the online website and other services.
          </AlertDescription>
        </Alert>

        <Box bg={bgCard} borderRadius="lg" boxShadow="sm" border="1px" borderColor={borderColor} p={6}>
          <VStack spacing={4} align="stretch">
            <Text fontSize="md" fontWeight="semibold" color={textColor}>
              Google Maps
            </Text>

            <FormControl>
              <FormLabel>Google Maps API Key</FormLabel>
              <InputGroup>
                <Input
                  type={showMapsApiKey ? "text" : "password"}
                  value={mapsApiKey}
                  onChange={(e) => setMapsApiKey(e.target.value)}
                  placeholder="Paste Google Maps API key"
                  bg="white"
                />
                <InputRightElement>
                  <IconButton
                    aria-label={showMapsApiKey ? "Hide" : "Show"}
                    icon={showMapsApiKey ? <ViewOffIcon /> : <ViewIcon />}
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowMapsApiKey((v) => !v)}
                  />
                </InputRightElement>
              </InputGroup>
              <Text fontSize="xs" color={textSecondary} mt={1}>
                Common services for online ordering: Maps JavaScript API, Places API (autocomplete), Geocoding API (address validation).
              </Text>
            </FormControl>

            <FormControl>
              <FormLabel>Browser Key (Maps JS + Places Autocomplete)</FormLabel>
              <InputGroup>
                <Input
                  type={showMapsBrowserKey ? "text" : "password"}
                  value={mapsBrowserKey}
                  onChange={(e) => setMapsBrowserKey(e.target.value)}
                  placeholder="Paste restricted browser key (HTTP referrer restricted)"
                  bg="white"
                />
                <InputRightElement>
                  <IconButton
                    aria-label={showMapsBrowserKey ? "Hide" : "Show"}
                    icon={showMapsBrowserKey ? <ViewOffIcon /> : <ViewIcon />}
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowMapsBrowserKey((v) => !v)}
                  />
                </InputRightElement>
              </InputGroup>
              <Text fontSize="xs" color={textSecondary} mt={1}>
                This key is used by the online website to load Places Autocomplete in the browser. Restrict it by domain (HTTP referrers) and limit APIs to Maps JavaScript + Places.
              </Text>
            </FormControl>

            <FormControl>
              <FormLabel>Places API Key (optional)</FormLabel>
              <Input
                value={placesApiKey}
                onChange={(e) => setPlacesApiKey(e.target.value)}
                placeholder="Optional separate key for Places API"
                bg="white"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Geocoding API Key (optional)</FormLabel>
              <Input
                value={geocodingApiKey}
                onChange={(e) => setGeocodingApiKey(e.target.value)}
                placeholder="Optional separate key for Geocoding API"
                bg="white"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Directions API Key (optional)</FormLabel>
              <Input
                value={directionsApiKey}
                onChange={(e) => setDirectionsApiKey(e.target.value)}
                placeholder="Optional separate key for Directions API"
                bg="white"
              />
              <Text fontSize="xs" color={textSecondary} mt={1}>
                Useful later for delivery ETA / route estimation.
              </Text>
            </FormControl>

            <FormControl>
              <FormLabel>Address Validation API Key (server-side)</FormLabel>
              <InputGroup>
                <Input
                  type={showAddressValidationApiKey ? "text" : "password"}
                  value={addressValidationApiKey}
                  onChange={(e) => setAddressValidationApiKey(e.target.value)}
                  placeholder="Paste Address Validation API key (keep private)"
                  bg="white"
                />
                <InputRightElement>
                  <IconButton
                    aria-label={showAddressValidationApiKey ? "Hide" : "Show"}
                    icon={showAddressValidationApiKey ? <ViewOffIcon /> : <ViewIcon />}
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowAddressValidationApiKey((v) => !v)}
                  />
                </InputRightElement>
              </InputGroup>
              <Text fontSize="xs" color={textSecondary} mt={1}>
                Used by Cloud Functions to validate delivery addresses. Do not expose this key in frontend code.
              </Text>
            </FormControl>

            <FormControl>
              <FormLabel>Google Analytics Measurement ID (optional)</FormLabel>
              <Input
                value={analyticsMeasurementId}
                onChange={(e) => setAnalyticsMeasurementId(e.target.value)}
                placeholder="e.g., G-XXXXXXXXXX"
                bg="white"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Google Tag Manager Container ID (optional)</FormLabel>
              <Input
                value={tagManagerContainerId}
                onChange={(e) => setTagManagerContainerId(e.target.value)}
                placeholder="e.g., GTM-XXXXXXX"
                bg="white"
              />
            </FormControl>

            <Button alignSelf="flex-start" onClick={handleSaveMaps} isLoading={savingMaps} loadingText="Saving...">
              Save Google Maps
            </Button>
          </VStack>
        </Box>

        <Box bg={bgCard} borderRadius="lg" boxShadow="sm" border="1px" borderColor={borderColor} p={6}>
          <VStack spacing={4} align="stretch">
            <Text fontSize="md" fontWeight="semibold" color={textColor}>
              reCAPTCHA
            </Text>

            <FormControl>
              <FormLabel>reCAPTCHA Site Key</FormLabel>
              <Input
                value={recaptchaSiteKey}
                onChange={(e) => setRecaptchaSiteKey(e.target.value)}
                placeholder="Paste reCAPTCHA site key"
                bg="white"
              />
              <Text fontSize="xs" color={textSecondary} mt={1}>
                The site key is safe to expose in the website frontend.
              </Text>
            </FormControl>

            <FormControl>
              <FormLabel>reCAPTCHA Secret Key</FormLabel>
              <InputGroup>
                <Input
                  type={showRecaptchaSecretKey ? "text" : "password"}
                  value={recaptchaSecretKey}
                  onChange={(e) => setRecaptchaSecretKey(e.target.value)}
                  placeholder="Paste reCAPTCHA secret key"
                  bg="white"
                />
                <InputRightElement>
                  <IconButton
                    aria-label={showRecaptchaSecretKey ? "Hide" : "Show"}
                    icon={showRecaptchaSecretKey ? <ViewOffIcon /> : <ViewIcon />}
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowRecaptchaSecretKey((v) => !v)}
                  />
                </InputRightElement>
              </InputGroup>
              <Text fontSize="xs" color={textSecondary} mt={1}>
                Keep the secret key server-side only (Cloud Functions / backend verification).
              </Text>
            </FormControl>

            <Button alignSelf="flex-start" onClick={handleSaveRecaptcha} isLoading={savingRecaptcha} loadingText="Saving...">
              Save reCAPTCHA
            </Button>
          </VStack>
        </Box>

        <Box bg={bgCard} borderRadius="lg" boxShadow="sm" border="1px" borderColor={borderColor} p={6}>
          <VStack spacing={4} align="stretch">
            <Text fontSize="md" fontWeight="semibold" color={textColor}>
              Text-to-Speech
            </Text>

            <FormControl display="flex" alignItems="center" justifyContent="space-between">
              <FormLabel mb={0}>Enable Google Text-to-Speech</FormLabel>
              <Switch isChecked={ttsEnabled} onChange={(e) => setTtsEnabled(e.target.checked)} />
            </FormControl>

            <FormControl>
              <FormLabel>Text-to-Speech API Key</FormLabel>
              <InputGroup>
                <Input
                  type={showTtsApiKey ? "text" : "password"}
                  value={ttsApiKey}
                  onChange={(e) => setTtsApiKey(e.target.value)}
                  placeholder="Paste Google Text-to-Speech API key"
                  bg="white"
                />
                <InputRightElement>
                  <IconButton
                    aria-label={showTtsApiKey ? "Hide" : "Show"}
                    icon={showTtsApiKey ? <ViewOffIcon /> : <ViewIcon />}
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowTtsApiKey((v) => !v)}
                  />
                </InputRightElement>
              </InputGroup>
              <Text fontSize="xs" color={textSecondary} mt={1}>
                Used by RMS POS to speak new orders on the local device. Restrict this key to the Cloud Text-to-Speech API.
              </Text>
            </FormControl>

            <Button alignSelf="flex-start" onClick={handleSaveTts} isLoading={savingTts} loadingText="Saving...">
              Save Text-to-Speech
            </Button>
          </VStack>
        </Box>

        <Box bg={bgCard} borderRadius="lg" boxShadow="sm" border="1px" borderColor={borderColor} p={6}>
          <VStack spacing={4} align="stretch">
            <Text fontSize="md" fontWeight="semibold" color={textColor}>
              Cloud Translation
            </Text>

            <FormControl>
              <FormLabel>Cloud Translation API Key</FormLabel>
              <InputGroup>
                <Input
                  type={showTranslateApiKey ? "text" : "password"}
                  value={translateApiKey}
                  onChange={(e) => setTranslateApiKey(e.target.value)}
                  placeholder="Paste Google Cloud Translation API key"
                  bg="white"
                />
                <InputRightElement>
                  <IconButton
                    aria-label={showTranslateApiKey ? "Hide" : "Show"}
                    icon={showTranslateApiKey ? <ViewOffIcon /> : <ViewIcon />}
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowTranslateApiKey((v) => !v)}
                  />
                </InputRightElement>
              </InputGroup>
              <Text fontSize="xs" color={textSecondary} mt={1}>
                Used by Store Announcement to translate English and French. In Google Cloud, enable Cloud Translation API, then create an API key restricted to that API. The Maps and Text-to-Speech keys cannot be used for this.
              </Text>
            </FormControl>

            <Button alignSelf="flex-start" onClick={handleSaveTranslate} isLoading={savingTranslate} loadingText="Saving...">
              Save Cloud Translation
            </Button>
          </VStack>
        </Box>
      </VStack>
    </PageShell>
  );
};

export default GoogleSettings;
